import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Plus, Trash2, Clock, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { id: 0, ar: 'الأحد', en: 'Sunday', fr: 'Dimanche' },
  { id: 1, ar: 'الإثنين', en: 'Monday', fr: 'Lundi' },
  { id: 2, ar: 'الثلاثاء', en: 'Tuesday', fr: 'Mardi' },
  { id: 3, ar: 'الأربعاء', en: 'Wednesday', fr: 'Mercredi' },
  { id: 4, ar: 'الخميس', en: 'Thursday', fr: 'Jeudi' },
  { id: 5, ar: 'الجمعة', en: 'Friday', fr: 'Vendredi' },
  { id: 6, ar: 'السبت', en: 'Saturday', fr: 'Samedi' }
];

export const AvailabilityEditor = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [slots, setSlots] = useState([]);
  const slotKeySeq = React.useRef(0);
  const withKey = (s) => ({ ...s, _key: s._key || `slot-${++slotKeySeq.current}` });
  const [duration, setDuration] = useState(30);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/doctors/${user._id}/availability`)
      .then(r => {
        setSlots((r.data.slots || []).map(withKey));
        setDuration(r.data.slot_duration_minutes || 30);
      })
      .catch((err) => {
        // Doctor may not yet have an availability record — non-blocking
        console.warn('availability load failed', err);
      });
  }, [user]);

  const addSlot = () => {
    setSlots([...slots, withKey({ day_of_week: 1, start_time: '09:00', end_time: '17:00' })]);
  };

  const updateSlot = (idx, field, value) => {
    const next = [...slots];
    next[idx] = { ...next[idx], [field]: field === 'day_of_week' ? parseInt(value) : value };
    setSlots(next);
  };

  const removeSlot = (idx) => {
    setSlots(slots.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setLoading(true);
    setMsg({ type: '', text: '' });
    try {
      // Strip local-only _key before sending to backend
      const cleanSlots = slots.map(({ _key, ...rest }) => rest);
      await axios.put(`${API}/doctors/me/availability`, {
        slots: cleanSlots,
        slot_duration_minutes: duration
      }, { withCredentials: true });
      setMsg({ type: 'success', text: t('تم حفظ جدولك بنجاح', 'Schedule saved successfully', 'Planning enregistré') });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.detail || t('حدث خطأ', 'An error occurred', 'Erreur') });
    } finally {
      setLoading(false);
    }
  };

  const dayLabel = (id) => {
    const d = DAYS.find(x => x.id === id);
    return language === 'ar' ? d.ar : language === 'fr' ? d.fr : d.en;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-[#2c3e50] flex items-center gap-2">
          <Clock size={20} className="text-[#1e88e5]" />
          {t('جدول التوفر الأسبوعي', 'Weekly Availability', 'Disponibilité hebdomadaire')}
        </h3>
        <Button onClick={addSlot} variant="outline" data-testid="add-slot-button">
          <Plus size={16} className={language === 'ar' ? 'ml-2' : 'mr-2'} />
          {t('إضافة فترة', 'Add Time Block', 'Ajouter')}
        </Button>
      </div>

      {msg.text && (
        <Alert variant={msg.type === 'error' ? 'destructive' : 'default'} className={msg.type === 'success' ? 'bg-green-50 border-green-200' : ''}>
          <AlertDescription className={msg.type === 'success' ? 'text-green-800' : ''}>{msg.text}</AlertDescription>
        </Alert>
      )}

      <Card className="p-4">
        <Label>{t('مدة الموعد الواحد (بالدقائق)', 'Appointment Duration (minutes)', 'Durée du rendez-vous')}</Label>
        <Select value={String(duration)} onValueChange={(v) => setDuration(parseInt(v))}>
          <SelectTrigger className="mt-2 max-w-xs" data-testid="slot-duration-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="15">15 {t('دقيقة', 'min', 'min')}</SelectItem>
            <SelectItem value="30">30 {t('دقيقة', 'min', 'min')}</SelectItem>
            <SelectItem value="45">45 {t('دقيقة', 'min', 'min')}</SelectItem>
            <SelectItem value="60">60 {t('دقيقة', 'min', 'min')}</SelectItem>
          </SelectContent>
        </Select>
      </Card>

      {slots.length === 0 ? (
        <Card className="p-6 text-center">
          <p className="text-gray-600 mb-2">{t('لم تقم بتحديد ساعات عملك بعد', 'You have not set your working hours yet', 'Aucun horaire défini')}</p>
          <p className="text-sm text-gray-500">{t('اضغط "إضافة فترة" لبدء استقبال المواعيد', 'Click "Add Time Block" to start accepting appointments', 'Cliquez sur "Ajouter"')}</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {slots.map((slot, idx) => (
            <Card key={slot._key} className="p-4" data-testid={`slot-${idx}`}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                <div>
                  <Label>{t('اليوم', 'Day', 'Jour')}</Label>
                  <Select value={String(slot.day_of_week)} onValueChange={(v) => updateSlot(idx, 'day_of_week', v)}>
                    <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DAYS.map(d => <SelectItem key={d.id} value={String(d.id)}>{dayLabel(d.id)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('من', 'From', 'De')}</Label>
                  <Input type="time" value={slot.start_time} onChange={(e) => updateSlot(idx, 'start_time', e.target.value)} className="mt-2" data-testid={`slot-start-${idx}`} />
                </div>
                <div>
                  <Label>{t('إلى', 'To', 'À')}</Label>
                  <Input type="time" value={slot.end_time} onChange={(e) => updateSlot(idx, 'end_time', e.target.value)} className="mt-2" data-testid={`slot-end-${idx}`} />
                </div>
                <Button variant="ghost" onClick={() => removeSlot(idx)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`remove-slot-${idx}`}>
                  <Trash2 size={18} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Button onClick={handleSave} disabled={loading} className="w-full bg-[#1e88e5] hover:bg-[#1565c0]" data-testid="save-availability-button">
        <Save className={language === 'ar' ? 'ml-2' : 'mr-2'} size={18} />
        {loading ? t('جاري الحفظ...', 'Saving...', 'Enregistrement...') : t('حفظ الجدول', 'Save Schedule', 'Enregistrer')}
      </Button>
    </div>
  );
};
